/**
 * Account Card Component
 *
 * Mobile-friendly card view for accounts.
 * Displays account info in a compact card format.
 */
import { Card, CardContent, Checkbox } from '@respectly/ui';
import { cn } from '@respectly/ui';
import { Building2, Users, Calendar, User } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import type { AccountListItem } from '@respectly/shared-types';
import { AccountStatusBadge, BillingStatusBadge } from './AccountStatusBadge';
import { AccountQuickActions } from './AccountQuickActions';
import { useAccountsStore } from '../../stores/useAccountsStore';

// ============================================
// Types
// ============================================

interface AccountCardProps {
  account: AccountListItem;
  onImpersonate?: () => void;
  className?: string;
}

// ============================================
// Utility
// ============================================

function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// ============================================
// Component
// ============================================

export function AccountCard({ account, onImpersonate, className }: AccountCardProps) {
  const navigate = useNavigate();
  const { selectedIds, toggleSelect } = useAccountsStore();

  const isSelected = selectedIds.has(account.id);

  const handleCardClick = () => {
    navigate({ to: '/accounts/$id', params: { id: account.id } });
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleSelect(account.id);
  };

  return (
    <Card
      className={cn(
        'cursor-pointer transition-all duration-200',
        'hover:shadow-md hover:border-slate-300 dark:hover:border-slate-600',
        isSelected && 'ring-2 ring-blue-500 ring-offset-2',
        className
      )}
      onClick={handleCardClick}
    >
      <CardContent className="p-4">
        {/* Header Row */}
        <div className="flex items-start justify-between gap-3">
          {/* Left: Checkbox + Avatar + Name */}
          <div className="flex items-start gap-3 min-w-0">
            {/* Checkbox */}
            <div onClick={handleCheckboxClick}>
              <Checkbox checked={isSelected} className="mt-1" />
            </div>

            {/* Avatar */}
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 text-sm font-semibold text-slate-600 dark:from-slate-700 dark:to-slate-800 dark:text-slate-300">
              {getInitials(account.name)}
            </div>

            {/* Name & Industry */}
            <div className="min-w-0">
              <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                {account.name}
              </h3>
              {account.industry && (
                <p className="text-xs text-slate-500 truncate">{account.industry}</p>
              )}
              <p className="text-xs text-slate-400 truncate">
                {account.ownerEmail ?? `/${account.slug}`}
              </p>
            </div>
          </div>

          {/* Right: Actions */}
          <div onClick={(e) => e.stopPropagation()}>
            <AccountQuickActions account={account} onImpersonate={onImpersonate} />
          </div>
        </div>

        {/* Status Badges Row */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <AccountStatusBadge status={account.status} size="sm" />
          <BillingStatusBadge status={account.billingStatus} size="sm" />
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">
            {account.plan.name}
          </span>
        </div>

        {/* Metadata Row */}
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-500">
          {/* Users Count */}
          <div className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" />
            <span>{account.userCount} users</span>
          </div>

          {/* Created Date */}
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            <span>{formatDate(account.createdAt)}</span>
          </div>

          {/* Contacts Count */}
          <div className="flex items-center gap-1.5">
            <Building2 className="h-3.5 w-3.5" />
            <span>{account.contactCount} contacts</span>
          </div>

          {/* Assigned To */}
          {account.assignedTo && (
            <div className="flex items-center gap-1.5 truncate">
              <User className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">
                {account.assignedTo.firstName} {account.assignedTo.lastName}
              </span>
            </div>
          )}
        </div>

        {/* Trial End Warning */}
        {account.status === 'trial' && account.trialEndsAt && (
          <div className="mt-3 rounded-md bg-blue-50 px-2.5 py-1.5 text-xs text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
            Trial ends {formatDate(account.trialEndsAt)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================
// Account Card Grid
// ============================================

interface AccountCardGridProps {
  accounts: AccountListItem[];
  onImpersonate?: (accountId: string) => void;
  className?: string;
}

export function AccountCardGrid({ accounts, onImpersonate, className }: AccountCardGridProps) {
  if (accounts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Building2 className="h-12 w-12 text-slate-300 dark:text-slate-600" />
        <h3 className="mt-4 text-lg font-medium text-slate-900 dark:text-white">
          No accounts found
        </h3>
        <p className="mt-1 text-sm text-slate-500">
          Try adjusting your filters or create a new account.
        </p>
      </div>
    );
  }

  return (
    <div className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-3', className)}>
      {accounts.map((account) => (
        <AccountCard
          key={account.id}
          account={account}
          onImpersonate={onImpersonate ? () => onImpersonate(account.id) : undefined}
        />
      ))}
    </div>
  );
}
